import React, { useState, useEffect } from 'react';
import { Table, Button, Space, message, Popconfirm, Tag, Input } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, BellOutlined, ReloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { ProForm, ProFormText, ProFormSelect, ProFormTextArea, ProFormDateTimePicker, ModalForm } from '@ant-design/pro-components';

interface Announcement {
  id: number;
  title: string;
  source_from: string;
  content: string;
  publish_date: string;
  article_type: number;
  status_flag: number;
  create_time: string;
}

export default function Announcements() {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState<Announcement | null>(null);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [form] = ProForm.useForm();

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const fetchAnnouncements = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const resp = await fetch('/api/admin/announcements', {
        headers: { 'Authorization': `Bearer ${token}` },
      }).then(r => r.json());

      if (resp && resp.ok) {
        setAnnouncements(resp.data || []);
      } else {
        message.error(resp?.error || '获取公告列表失败');
      }
    } catch (err) {
      message.error('获取公告列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingAnnouncement(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record: Announcement) => {
    setEditingAnnouncement(record);
    form.setFieldsValue({
      ...record,
      publish_date: record.publish_date ? new Date(record.publish_date) : undefined,
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      const token = localStorage.getItem('admin_token');
      const resp = await fetch(`/api/admin/announcements/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      }).then(r => r.json());

      if (resp && resp.ok) {
        message.success('删除成功');
        fetchAnnouncements();
      } else {
        message.error(resp?.error || '删除失败');
      }
    } catch (err) {
      message.error('删除失败');
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      const token = localStorage.getItem('admin_token');
      const url = editingAnnouncement 
        ? `/api/admin/announcements/${editingAnnouncement.id}`
        : '/api/admin/announcements';
      const method = editingAnnouncement ? 'PUT' : 'POST';

      const resp = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(values),
      }).then(r => r.json());

      if (resp && resp.ok) {
        message.success(editingAnnouncement ? '更新成功' : '创建成功');
        setModalVisible(false);
        fetchAnnouncements();
      } else {
        message.error(resp?.error || '操作失败');
      }
    } catch (err) {
      message.error('操作失败');
    }
  };

  // 过滤数据
  const filteredAnnouncements = announcements.filter(a => 
    a.title?.toLowerCase().includes(searchKeyword.toLowerCase()) ||
    a.source_from?.toLowerCase().includes(searchKeyword.toLowerCase())
  );

  // 获取类型颜色
  const getTypeTag = (type: number) => {
    const types: Record<number, { text: string; color: string }> = {
      1: { text: '公司动态', color: 'blue' },
      2: { text: '行业新闻', color: 'green' },
      3: { text: '政策法规', color: 'purple' },
    };
    const t = types[type] || { text: '未知', color: 'default' };
    return <Tag color={t.color} style={{ borderRadius: 12 }}>{t.text}</Tag>;
  };

  const columns: ColumnsType<Announcement> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 70,
      render: (id) => <span style={{ color: '#6b7280', fontFamily: 'monospace' }}>#{id}</span>
    },
    {
      title: '标题',
      dataIndex: 'title',
      key: 'title',
      render: (text) => <span style={{ fontWeight: 500, color: '#1f2937' }}>{text}</span>
    },
    {
      title: '来源',
      dataIndex: 'source_from',
      key: 'source_from',
    },
    {
      title: '文章类型',
      dataIndex: 'article_type',
      key: 'article_type',
      render: (type: number) => getTypeTag(type)
    },
    {
      title: '发布时间',
      dataIndex: 'publish_date',
      key: 'publish_date',
      render: (text) => <span style={{ color: '#6b7280', fontSize: 13 }}>{text}</span>
    },
    {
      title: '创建时间',
      dataIndex: 'create_time',
      key: 'create_time',
      render: (text) => <span style={{ color: '#6b7280', fontSize: 13 }}>{text}</span>
    },
    {
      title: '操作',
      key: 'action',
      width: 160,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            style={{ color: '#3b82f6' }}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这个公告吗？"
            description="删除后将无法恢复"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
            okButtonProps={{ danger: true }}
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="admin-page">
      {/* 页面头部 */}
      <div className="admin-page-header">
        <div className="admin-page-title">
          <div className="admin-page-title-icon">
            <BellOutlined />
          </div>
          <div>
            <h2>公告管理</h2>
            <p>管理系统公告和新闻</p>
          </div>
        </div>
        
        <Space size="middle">
          <Input
            placeholder="搜索公告..."
            prefix={<SearchOutlined style={{ color: '#9ca3af' }} />}
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            style={{ width: 220, borderRadius: 10 }}
            allowClear
          />
          <Button 
            icon={<ReloadOutlined />} 
            onClick={fetchAnnouncements}
            style={{ borderRadius: 10 }}
          >
            刷新
          </Button>
          <Button 
            type="primary" 
            icon={<PlusOutlined />} 
            onClick={handleAdd}
            style={{ 
              borderRadius: 10,
              background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
              border: 'none',
              boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
            }}
          >
            新增公告
          </Button>
        </Space>
      </div>
      
      {/* 表格内容 */}
      <div className="admin-page-content">
        <div style={{ marginBottom: 16, color: '#6b7280', fontSize: 14 }}>
          共 <span style={{ color: '#3b82f6', fontWeight: 600 }}>{filteredAnnouncements.length}</span> 条记录
        </div>
        
        <Table
          columns={columns}
          dataSource={filteredAnnouncements}
          loading={loading}
          rowKey="id"
          className="admin-table"
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
          }}
        />
      </div>
      
      {/* 编辑弹窗 */}
      <ModalForm
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <BellOutlined style={{ color: 'white', fontSize: 16 }} />
            </div>
            <span>{editingAnnouncement ? '编辑公告' : '新增公告'}</span>
          </div>
        }
        open={modalVisible}
        onOpenChange={setModalVisible}
        form={form}
        onFinish={handleSubmit}
        width={700}
        modalProps={{
          destroyOnHidden: true,
          centered: true,
        }}
      >
        <div style={{ marginTop: 24 }}>
          <ProFormText
            name="title"
            label="标题"
            placeholder="请输入公告标题"
            rules={[{ required: true, message: '请输入标题' }]}
          />
          <ProFormText
            name="source_from"
            label="来源"
            placeholder="请输入来源"
          />
          <ProFormSelect
            name="article_type"
            label="文章类型"
            placeholder="请选择文章类型"
            options={[
              { label: '公司动态', value: 1 },
              { label: '行业新闻', value: 2 },
              { label: '政策法规', value: 3 },
            ]}
          />
          <ProFormTextArea
            name="content"
            label="内容"
            placeholder="请输入公告内容"
            fieldProps={{ rows: 8 }}
          />
          <ProFormDateTimePicker
            name="publish_date"
            label="发布时间"
            placeholder="请选择发布时间"
          />
        </div>
      </ModalForm>
    </div>
  );
}
